'use client'

import { FunctionComponent, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { applyPromoCodeAction } from '@/app/_actions/cart.actions'
import { unwrapActionResult } from '@/utils/errors.utils'
import { useSeller } from '@/app/_providers/SellerProvider/SellerProvider'
import TheGreatContext from '@/app/_providers/TheGreatProvider/TheGreatContext'
import { cartExists } from '@/utils/cart.utils'
import UrlPromoCodeContext from './UrlPromoCodeContext'
import { expandPromoCodesForPreview } from '@/utils/providers/UrlPromoCodeProvider.utils'
import { QUERY_KEYS } from '@/config/tanstack-querykeys.config'
import { useCart } from '@/app/_providers/CartProvider/CartProvider'
import { logger } from '@/utils/logger'

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

const { error: logError } = logger()

/**
 * Handles incoming promo codes from URL parameters and applies them to the cart
 * once the cart is initialised and contains line items.
 *
 * **Behaviour**
 * - Listens for promo codes passed in via `onLoadData` (URL params parsed by TheGreatProvider).
 * - Once a cart exists **and** has line items, fires the `applyPromoCode` mutation exactly once
 *   (guarded by the `redeeming` flag to prevent duplicate calls).
 * - On success the pending promo codes are cleared so the mutation cannot re-fire.
 * - Code augmentation via `expandPromoCodesForPreview` expands each unique code once per line-item
 *   quantity, but **only** for PREVIEW sites.
 *
 * **Why the run-once guard?**
 * The effect watching `cart` will re-fire as the cart updates. Without the `redeeming` flag
 * the mutation would be called on every subsequent cart change after promo codes arrive,
 * potentially applying the same code multiple times.
 *
 * **NOTE:** This functionality may change — the ideal approach is to pass promo codes directly
 * to the cart creation call, enabling block-add-to-cart cart rules to be evaluated immediately.
 *
 * @param {PropsWithChildren} props
 */
const UrlPromoCodeProvider: FunctionComponent<PropsWithChildren> = (
  { children } : PropsWithChildren,
) => {
  const queryClient = useQueryClient()

  const { isPreview } = useSeller()
  const {
    incomingQueries: { promoCodes: pendingCodes },
    handleSetIncomingPromoCodes,
  } = useContext(TheGreatContext)
  const { cart, loading } = useCart()
  const redeemingRef = useRef(false)

  const setRedeeming = useCallback((nextValue: boolean) => {
    redeemingRef.current = nextValue
  }, [])

  const { mutate: applyPromoCode, isPending: redeemLoading } = useMutation({
    mutationFn: ({ cartId, codes, sellerId }: {
      cartId: string; codes: string[]; sellerId: string
    }) => applyPromoCodeAction(cartId, codes, sellerId)
      .then(unwrapActionResult),

    onSuccess: (cartResponse) => {
      queryClient.setQueryData([QUERY_KEYS.CART, cartResponse.id], cartResponse)
      // Clear pending codes so this mutation cannot fire again
      handleSetIncomingPromoCodes([])
    },
    onError: (error, variables) => {
      redeemingRef.current = false
      handleSetIncomingPromoCodes([])

      logError('[UrlPromoCodeProvider] Failed to apply promo code(s)', {
        error,
        cartId: variables.cartId,
        codes: variables.codes,
        sellerId: variables.sellerId,
      })
    },
  })

  // Fire the mutation exactly once when the cart has loaded and contains line items.
  // The `redeeming` flag prevents re-firing when the cart updates after the mutation.
  useEffect(() => {
    if (
      !loading
      && cartExists(cart)
      && cart.lineItems.length > 0
      && pendingCodes
      && pendingCodes.length > 0
      && !redeemingRef.current
    ) {
      const expandedCodes = expandPromoCodesForPreview({
        promoCodes: pendingCodes,
        isPreview,
        lineItems: cart.lineItems,
      })

      redeemingRef.current = true

      applyPromoCode({
        cartId: cart.id,
        codes: expandedCodes,
        sellerId: cart.sellerId ?? '',
      })
    }
  }, [cart, pendingCodes, isPreview, loading, applyPromoCode])

  const value = useMemo(
    () => ({ setRedeeming, redeemLoading }),
    [redeemLoading, setRedeeming],
  )

  return (
    <UrlPromoCodeContext.Provider value={value}>
      {children}
    </UrlPromoCodeContext.Provider>
  )
}

export default UrlPromoCodeProvider
